import { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';

interface AdminConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title?: string;
  message?: string;
  actionLabel?: string;
  isLoading?: boolean;
}

export function AdminConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm this action',
  message = 'Continue with this change?',
  actionLabel = 'Confirm',
  isLoading = false,
}: AdminConfirmModalProps) {
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSubmitError('');
    }
  }, [isOpen]);

  const submit = async () => {
    if (isLoading) return;
    setSubmitError('');
    try {
      await onConfirm();
    } catch (caught) {
      setSubmitError(
        caught instanceof Error
          ? caught.message
          : 'The action could not be completed.'
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      showCloseButton={false}
      closeOnOverlayClick={!isLoading}
      closeOnEscape={!isLoading}
      title={title}
    >
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--amy-accent)]/10 dark:bg-[var(--amy-accent)]/20">
          <ShieldCheck className="h-6 w-6 text-[var(--amy-accent)] dark:text-[var(--amy-accent)]" />
        </div>
        <p className="mb-6 text-sm text-gray-500 dark:text-[var(--amy-muted)]">
          {message}
        </p>
        {submitError && (
          <p
            role="alert"
            className="mb-6 text-left text-sm text-red-600 dark:text-red-400"
          >
            {submitError}
          </p>
        )}
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="flex-1"
            onClick={() => void submit()}
            isLoading={isLoading}
          >
            {actionLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
